import { v4 as uuid } from 'uuid'
import { Semaphore } from 'async-mutex'
import {
  type PhotoPreviewBlobs,
  uploadPhotoWithRetries,
} from 'services/photoUploader'
import { logger } from 'services/logger'
import { extractErrorMessage } from 'utils/ApiUtils'
import { removeImageFromCache } from 'mixtiles-web-common/services/imageLoader'
import {
  detectFacesFromFiles,
  detectFacesFromUrl,
} from 'components/PhotoBank/faces'
import {
  detectPetsFromFiles,
  detectPetsFromUrl,
} from 'components/PhotoBank/pets'
import { createThumbWithRatio } from 'services/frameThumbsCreator'
import {
  getImageHashFromFile,
  getImageHashFromUrl,
} from 'components/PhotoBank/imageHash'
import { getImageDataByUrl } from 'utils/ImageUtils'
import { importConvertHeicFile, isHeicFile } from 'services/lazy-heic'
import { createThumbnail } from 'components/PhotoBank/photoCompress'
import { importCompressor } from 'components/PhotoBank/lazy-compressorjs'
import { isClient } from 'utils/runtimeUtils'
import { calculatePhotoThumbDimensions } from 'components/PhotoBank/PhotoBank.utils'
import { getDownscaledDimensions } from 'lib/image/utils/downscale'
import type {
  Dimensions,
  FaceDetectionResult,
  LocalPhoto,
  PetDetectionResult,
} from 'components/PhotoBank/PhotoBank.types'

// The pet model letterboxes every input up to 640px, so 400px costs the analyzer the same as
// the 250px thumb while finding more pets (77% → 89% cat/dog recall on hand-labeled photos).
const PET_DETECTION_THUMB_MAX_SIDE = 400
const PET_DETECTION_THUMB_QUALITY = 0.9
const PREVIEW_JPEG_QUALITY = 0.85
// Making previews decodes the full-res photo and keeps them until the upload ends. Preparing every photo of a big
// batch at once ran iOS Safari out of memory, so only as many photos as upload at once are prepared.
const PREPARED_UPLOADS_LIMIT = 5
const preparedUploadsSemaphore = new Semaphore(PREPARED_UPLOADS_LIMIT)

export type ProcessedFileResult =
  | { type: 'known'; hashCode: string }
  | {
      type: 'new'
      localPhoto: LocalPhoto
      faceDetectionBlob: Blob
      petDetectionBlob?: Blob
    }

export interface IPhotoBankUploadService {
  uploadFileToS3(
    file: File,
    dimensions: { width: number; height: number },
    analyticsProps: Record<string, unknown>
  ): Promise<string>

  processFile(
    file: File,
    uploadSrc: string,
    knownHashes: Set<string>,
    includePetData: boolean
  ): Promise<ProcessedFileResult>

  detectFacesFromFiles(
    files: File[],
    includeHappyData: boolean,
    filterSmallFaces: boolean
  ): Promise<FaceDetectionResult[]>

  detectFacesFromUrl(
    urls: string[],
    includeHappyData: boolean,
    filterSmallFaces: boolean
  ): Promise<FaceDetectionResult[]>

  detectPetsFromFiles(files: Blob[]): Promise<PetDetectionResult[]>

  detectPetsFromUrl(urls: string[]): Promise<PetDetectionResult[]>

  getImageDimensions(url: string): Promise<{ width: number; height: number }>

  hashFile(file: File): Promise<string>

  hashUrl(url: string): Promise<string>
}

export class PhotoBankUploadService implements IPhotoBankUploadService {
  constructor() {
    if (isClient()) {
      setTimeout(() => importCompressor(), 3000)
    }
  }

  async uploadFileToS3(
    file: File,
    dimensions: { width: number; height: number },
    analyticsProps: Record<string, unknown>
  ): Promise<string> {
    return preparedUploadsSemaphore.runExclusive(async () => {
      const previews = await createPhotoPreviewsOrSkip(file)
      return uploadPhotoWithRetries({
        file,
        width: dimensions.width,
        height: dimensions.height,
        analyticsProps,
        previews,
      })
    })
  }

  async processFile(
    file: File,
    uploadSrc: string,
    knownHashes: Set<string>,
    includePetData: boolean
  ): Promise<ProcessedFileResult> {
    let processedFile = file
    if (isHeicFile(file)) {
      const convertHeicFile = await importConvertHeicFile()
      processedFile = await convertHeicFile(file)
    }

    const { file: compressedFile } = await createThumbnail(processedFile, {
      maxSizeMB: 32,
    })

    const hashCode = await getImageHashFromFile(compressedFile)

    if (knownHashes.has(hashCode)) {
      return { type: 'known', hashCode }
    }

    const localUrl = URL.createObjectURL(compressedFile)
    const { width, height, image } = await getImageDataByUrl(localUrl)

    const thumbDimensions = calculatePhotoThumbDimensions(width, height)

    const { thumbUrl: lowResUrl, thumbDimensions: lowResDimensions } =
      await createThumbWithRatio({
        image,
        width,
        height,
        thumbDimensions: thumbDimensions.low,
      })

    const {
      thumbUrl: minimalResUrl,
      blob: faceDetectionBlob,
      thumbDimensions: minimalResDimensions,
    } = await createThumbWithRatio({
      image,
      width,
      height,
      thumbDimensions: thumbDimensions.minimal,
    })

    const petDetectionBlob = includePetData
      ? await createPetDetectionBlob(image, width, height)
      : undefined

    const localPhoto: LocalPhoto = {
      width,
      height,
      isLocal: true,
      url: localUrl,
      minimalResUrl,
      lowResUrl,
      highResUrl: lowResUrl,
      localUrl,
      originalFile: compressedFile,
      uid: uuid(),
      hashCode,
      uploadSrc,
      wasUsed: false,
      faceDetectionSuccess: null,
      smallFacesFiltered: 0,
      petDetection: { status: 'skipped' },
      thumbDimensions: {
        high: lowResDimensions,
        low: lowResDimensions,
        minimal: minimalResDimensions,
      },
    }

    return { type: 'new', localPhoto, faceDetectionBlob, petDetectionBlob }
  }

  async detectFacesFromFiles(
    files: File[],
    includeHappyData: boolean,
    filterSmallFaces: boolean = false
  ) {
    return detectFacesFromFiles(files, includeHappyData, filterSmallFaces)
  }

  async detectFacesFromUrl(
    urls: string[],
    includeHappyData: boolean,
    filterSmallFaces: boolean = false
  ) {
    return detectFacesFromUrl(urls, includeHappyData, filterSmallFaces)
  }

  async detectPetsFromFiles(files: Blob[]) {
    return detectPetsFromFiles(files)
  }

  async detectPetsFromUrl(urls: string[]) {
    return detectPetsFromUrl(urls)
  }

  async getImageDimensions(
    url: string
  ): Promise<{ width: number; height: number }> {
    const { width, height } = await getImageDataByUrl(url)
    return { width, height }
  }

  async hashFile(file: File): Promise<string> {
    return getImageHashFromFile(file)
  }

  async hashUrl(url: string): Promise<string> {
    return getImageHashFromUrl(url)
  }
}

async function createPetDetectionBlob(
  image: HTMLImageElement,
  width: number,
  height: number
): Promise<Blob> {
  const { thumbUrl, blob } = await createThumbWithRatio({
    image,
    width,
    height,
    thumbDimensions: getDownscaledDimensions(
      width,
      height,
      PET_DETECTION_THUMB_MAX_SIDE
    ),
    type: 'image/jpeg',
    quality: PET_DETECTION_THUMB_QUALITY,
  })
  URL.revokeObjectURL(thumbUrl)
  return blob
}

// The backend builds the previews a photo is missing, so a photo the browser can't make them for still uploads
async function createPhotoPreviewsOrSkip(
  file: File
): Promise<PhotoPreviewBlobs | undefined> {
  try {
    return await createPhotoPreviews(file)
  } catch (e) {
    logger.warning('Failed creating photo previews', {
      message: extractErrorMessage(e),
    })
    return undefined
  }
}

// Sized like the thumbnails the photo bank shows, so a preview can stand in for them
async function createPhotoPreviews(file: File): Promise<PhotoPreviewBlobs> {
  const fileUrl = URL.createObjectURL(file)
  try {
    const { image, width, height } = await getImageDataByUrl(fileUrl)
    const thumbDimensions = calculatePhotoThumbDimensions(width, height)
    // createThumbWithRatio draws on one shared canvas, so the two previews are made one after the other
    const createPreview = (dimensions: Dimensions) =>
      createThumbWithRatio({
        image,
        width,
        height,
        thumbDimensions: dimensions,
        type: 'image/jpeg',
        quality: PREVIEW_JPEG_QUALITY,
      })
    const min = await createPreview(thumbDimensions.minimal)
    URL.revokeObjectURL(min.thumbUrl)
    const high = await createPreview(thumbDimensions.high)
    URL.revokeObjectURL(high.thumbUrl)
    return { min: min.blob, high: high.blob }
  } finally {
    URL.revokeObjectURL(fileUrl)
    removeImageFromCache(fileUrl)
  }
}
